import React from 'react';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/utility/chevron-up';
import Lozenge from '@atlaskit/lozenge';
import { Flex, xcss, Box, Anchor } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { PERISCOPE_ANALYTICS } from '../../constants';

type Appearance = 'default' | 'success' | 'warning';
type Trend = {
	isGoingUp: boolean;
	value: string | number;
};

type Props = {
	title: string;
	value: string | number;
	trend?: Trend;
	appearance?: Appearance;
	metricType: string;
	url: string;
};

const getLozengeAppearance = (appearance: Appearance) => {
	switch (appearance) {
		case 'success':
			return 'success';
		case 'warning':
			return 'moved';
		default:
			return 'default';
	}
};

const getTrend = (trend: Trend) => (
	<>
		{trend.isGoingUp ? (
			<ChevronUpIcon color="currentColor" label="up" />
		) : (
			<ChevronDownIcon color="currentColor" label="down" />
		)}
		&nbsp;{trend.value}%
	</>
);

export const InsightsWidget: React.FC<Props> = ({
	title,
	value,
	trend,
	appearance = 'default',
	metricType,
	url,
}) => {
	const analyticsAttributes: Attributes = {
		metricType,
	};

	return (
		<Anchor
			href={url}
			xcss={anchorStyles}
			onClick={(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(
					analyticsEvent,
					`${PERISCOPE_ANALYTICS}.insights.action`,
					analyticsAttributes,
				);
			}}
		>
			<Flex>
				<Box xcss={titleStyles}>{title}</Box>
			</Flex>
			<Flex direction="column" alignItems="end" gap="space.050">
				<Box xcss={[valueStyles, colorStylesLookup[appearance]]}>{value}</Box>
				{trend && (
					<Box>
						<Lozenge appearance={getLozengeAppearance(appearance)}>{getTrend(trend)}</Lozenge>
					</Box>
				)}
			</Flex>
		</Anchor>
	);
};

const anchorStyles = xcss({
	flex: '1 1 0',
	display: 'flex',
	width: '100%',
	justifyContent: 'space-between',

	borderRadius: 'border.radius.100',
	boxShadow: 'elevation.shadow.raised',
	border: `1px solid ${token('color.border')}`,

	backgroundColor: 'color.background.input',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',

	':hover': {
		cursor: 'pointer',
		backgroundColor: 'color.background.input.hovered',
		textDecoration: 'none',
	},
	':focus': {
		outline: `2px solid ${token('color.border.focused')}`,
	},

	textDecoration: 'none',
});

const titleStyles = xcss({
	color: 'color.text.subtlest',
	font: 'font.heading.xsmall',
	fontWeight: 'font.weight.medium',
	lineHeight: '16px',
});

const valueStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: 'font.heading.xlarge',
	fontWeight: 'font.weight.medium',
	lineHeight: '32px',
});

const colorStylesLookup = {
	success: xcss({
		color: 'color.text.success',
	}),
	warning: xcss({
		color: 'color.text.warning',
	}),
	default: xcss({
		color: 'color.text',
	}),
};
